import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Play, Heart, TrendUp } from '@phosphor-icons/react';

export default function VideoSidebar() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/posts/trending?limit=6')
      .then(({ data }) => setPosts(data.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="widget-card animate-pulse" data-testid="video-sidebar-loading">
        <div className="h-4 bg-muted rounded w-28 mb-4" />
        <div className="grid grid-cols-2 gap-2">
          {[...Array(4)].map((_, i) => <div key={i} className="aspect-[9/16] bg-muted rounded-sm" />)}
        </div>
      </div>
    );
  }

  if (!posts.length) {
    return (
      <div className="widget-card" data-testid="video-sidebar-empty">
        <div className="flex items-center gap-2 mb-3">
          <TrendUp size={16} weight="bold" className="text-primary" />
          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-sans font-semibold">
            Trending Videos
          </p>
        </div>
        <div className="text-center py-8">
          <Play size={28} weight="thin" className="mx-auto text-muted-foreground" />
          <p className="text-xs text-muted-foreground font-sans mt-2">No videos yet</p>
          <button
            onClick={() => navigate('/upload')}
            className="mt-3 text-xs text-primary font-semibold font-sans hover:text-[#083313] transition-colors"
            data-testid="video-sidebar-upload-btn"
          >
            Be the first to post
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="widget-card" data-testid="video-sidebar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendUp size={16} weight="bold" className="text-primary" />
          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-sans font-semibold">
            Trending Videos
          </p>
        </div>
        <button
          onClick={() => navigate('/explore')}
          className="text-[10px] text-primary font-semibold font-sans uppercase tracking-wide hover:text-[#083313] transition-colors"
          data-testid="video-sidebar-explore-link"
        >
          See All
        </button>
      </div>

      <div className="grid grid-cols-2 gap-1.5" data-testid="video-sidebar-grid">
        {posts.map(post => (
          <div
            key={post.post_id}
            className="aspect-[9/16] relative overflow-hidden rounded-sm bg-black cursor-pointer group"
            onClick={() => navigate('/')}
            data-testid={`video-thumb-${post.post_id}`}
          >
            {post.media_type === 'video' ? (
              <video
                src={`${API_BASE}/api/media/${post.media_path}`}
                className="w-full h-full object-cover"
                muted
                preload="metadata"
              />
            ) : (
              <img
                src={`${API_BASE}/api/media/${post.media_path}`}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-1.5 left-1.5 right-1.5">
              <p className="text-white text-[10px] font-sans font-medium truncate">
                @{post.user_name || 'golfer'}
              </p>
              <div className="flex items-center gap-1 mt-0.5">
                <Heart size={9} weight="fill" className="text-white/70" />
                <span className="text-white/70 text-[9px] font-sans">{post.likes_count || 0}</span>
              </div>
            </div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <Play size={20} weight="fill" className="text-white opacity-0 group-hover:opacity-80 transition-opacity" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
